import React, { useEffect, useState } from "react";
import CustomSectionTitle from "../SectionTitle/CustomSectionTitle";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Badge,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CustomSkeleton from "../Loading/CustomSkeleton";

interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
}

interface TabDataItem {
  data: any[];
  pagination: PaginationInfo;
}

interface TabData {
  [key: string]: TabDataItem;
}

interface CardTabProps {
  title?: string;
  tabs: Array<{
    key: string;
    label: string;
    count: number;
    hasNotification?: boolean;
    notificationCount?: number;
  }>;
  tabData: TabData;
  activeTab: number;
  onTabChange: (index: number) => void;
  loading?: boolean;
  loadingDelayMs?: number;
  children?: React.ReactNode;
  renderContent?: (tabData: TabDataItem, tabKey: string) => React.ReactNode;
}

const CardTab: React.FC<CardTabProps> = ({
  title,
  tabs,
  tabData,
  activeTab,
  onTabChange,
  loading = false,
  loadingDelayMs = 0,
  children,
  renderContent,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // true for xs
  const [showSkeleton, setShowSkeleton] = useState(
    loading && loadingDelayMs <= 0,
  );
  const shouldRenderLoadingLayout = loading || showSkeleton;

  useEffect(() => {
    if (!loading) {
      setShowSkeleton(false);
      return;
    }

    if (loadingDelayMs <= 0) {
      setShowSkeleton(true);
      return;
    }

    const timer = window.setTimeout(() => {
      setShowSkeleton(true);
    }, loadingDelayMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [loading, loadingDelayMs]);

  // Get current tab data
  const getCurrentTabData = () => {
    const maxTabs = tabs.length;
    const safeActiveTab = activeTab >= maxTabs ? 0 : activeTab;

    const currentTab = tabs[safeActiveTab];
    return (
      tabData[currentTab?.key] || {
        data: [],
        pagination: { total: 0, page: 1, limit: 50 },
      }
    );
  };

  // Content for each tab
  const renderTabContent = () => {
    const currentTabData = getCurrentTabData();
    const currentTab = tabs[activeTab];

    if (renderContent) {
      return renderContent(currentTabData, currentTab?.key || "");
    }

    if (children) {
      return children;
    }

    // 明示的なコンテンツ指定がない場合は何も表示しない
    return null;
  };

  return (
    <>
      {title ? (
        <Box sx={{ mb: 2 }}>
          <CustomSectionTitle title={title} addBorder={isMobile} />
        </Box>
      ) : null}

      {/* Tab Cards */}
      <Card
        sx={{
          backgroundColor: "#f5f5f5",
          boxShadow: "none",
          borderRadius: { xs: "10px", sm: "12px" },
        }}
      >
        <CardContent
          sx={{
            display: "flex",
            flexWrap: { xs: "wrap", sm: "wrap" },
            gap: 1,
            pb: 1,
            padding: "8px !important",
          }}
        >
          {tabs.map((tab, index) => (
            <Card
              key={index}
              data-testid={`card-tab-${tab.key}`}
              data-active={index === activeTab ? "true" : "false"}
              sx={{
                width: { xs: "calc(49% - 4px)", sm: "110px" },
                height: { xs: "96px", sm: "inherit" },
                cursor: shouldRenderLoadingLayout ? "default" : "pointer",
                backgroundColor:
                  index === activeTab ? "#bfe6f0" : "transparent",
                borderColor: index === activeTab ? "#bfe6f0" : "transparent",
                borderWidth: 1,
                boxShadow: "none",
                borderRadius: { xs: "8px", sm: "12px" },
                borderStyle: "solid",
                position: "relative",
                transition: "all 0.3s ease",
              }}
              onClick={() => {
                if (!shouldRenderLoadingLayout) onTabChange(index);
              }}
            >
                <CardContent
                  sx={{
                    textAlign: "center",
                    p: { xs: "8px", sm: "6px" },
                    ":last-child": { pb: { xs: "8px", sm: "6px" } },
                  }}
                >
                  {shouldRenderLoadingLayout ? (
                    <Box
                      sx={{
                        height: { xs: "16px", sm: "18px" },
                        display: "flex",
                        justifyContent: "center",
                        visibility: showSkeleton ? "visible" : "hidden",
                      }}
                    >
                      <CustomSkeleton width="70%" height={18} />
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: { xs: "11.28px", sm: "14px" },
                        lineHeight: { xs: "16px", sm: "18px" },
                        fontWeight: "bold",
                        color: index === activeTab ? "#0B9DBD" : "#3E3E3E",
                      }}
                    >
                      {tab.label}
                    </Typography>
                  )}
                  <Box sx={{ width: "100%", display: "inline-block" }}>
                    {tab.hasNotification ? (
                      <Badge
                        color="secondary"
                        overlap="circular"
                        badgeContent={""}
                        sx={{
                          width: "100%",
                          "& .MuiBadge-badge": {
                            right: { xs: "4px", sm: "5px" },
                            top: { xs: "6px", sm: "9px" },
                            width: { xs: "20px", sm: "30px" },
                            height: { xs: "20px", sm: "30px" },
                            borderRadius: "50%",
                            minWidth: { xs: "20px", sm: "30px" },
                            backgroundColor: "#FF0000",
                            border: "3px solid #F5F5F5",
                            fontSize: "12px",
                            fontWeight: "bold",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: "#fff",
                            mt: { xs: "5px", sm: "4px" },
                            width: { xs: "inherit !important", sm: "100px" },
                            height: { xs: "56.38px", sm: "50px" },
                            boxShadow: "none",
                            borderRadius: { xs: "8px", sm: "10px" },
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            "&:hover": {
                              boxShadow: "0px 0px 10px 0px #00000040",
                            },
                          }}
                        >
                          {shouldRenderLoadingLayout ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                visibility: showSkeleton
                                  ? "visible"
                                  : "hidden",
                              }}
                            >
                              <CustomSkeleton width="52px" height={28} />
                            </Box>
                          ) : (
                            <Typography
                              variant="h4"
                              align="center"
                              color="#3E3E3E"
                              sx={{
                                fontSize: { xs: "21px", sm: "20px" },
                                lineHeight: { xs: "28px", sm: "20px" },
                                fontWeight: 700,
                              }}
                            >
                              {tab.count}
                              <Typography
                                component="span"
                                variant="body2"
                                color="#3E3E3E"
                                fontWeight={700}
                                sx={{
                                  fontSize: { xs: "8px", sm: "12px" },
                                }}
                              >
                                件
                              </Typography>
                            </Typography>
                          )}
                        </Box>
                      </Badge>
                    ) : (
                      <Box
                        sx={{
                          backgroundColor: "#fff",
                          mt: { xs: "5px", sm: "4px" },
                          width: { xs: "inherit !important", sm: "100px" },
                          height: { xs: "56.38px", sm: "50px" },
                          borderRadius: { xs: "8px", sm: "10px" },
                          display: "flex",
                          justifyContent: "center",
                          boxShadow: "none",
                          "&:hover": {
                            boxShadow: "0px 0px 10px 0px #00000040",
                          },
                          alignItems: "center",
                        }}
                      >
                        {shouldRenderLoadingLayout ? (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              visibility: showSkeleton ? "visible" : "hidden",
                            }}
                          >
                            <CustomSkeleton width="52px" height={28} />
                          </Box>
                        ) : (
                          <Typography
                            variant="h4"
                            align="center"
                            sx={{
                              fontSize: { xs: "21px", sm: "20px" },
                              lineHeight: { xs: "28px", sm: "20px" },
                              fontWeight: 700,
                            }}
                          >
                            {tab.count}
                            <Typography
                              component="span"
                              variant="body2"
                              fontWeight={700}
                              sx={{
                                fontSize: { xs: "8px", sm: "12px" },
                              }}
                            >
                              件
                            </Typography>
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
        </CardContent>
      </Card>

      {/* Content Area */}
      {renderTabContent()}
    </>
  );
};

export default CardTab;
